/**
 * Analytical table (spec §109).
 *
 * Search, sort, column visibility, sticky header, CSV export, click-through.
 * Server-side pagination is supported through the `pagination` prop; nothing
 * here loads a large dataset into the browser.
 */
import { useMemo, useState } from "react";
import { ArrowDown, ArrowUp, Download, Search, Settings2 } from "lucide-react";

export interface Column<T> {
  key: string;
  header: string;
  align?: "left" | "right";
  /** Rendered cell. Return a string for CSV export to work without extra config. */
  render?: (row: T) => React.ReactNode;
  value?: (row: T) => string | number | null;
  sortable?: boolean;
  hidden?: boolean;
  width?: string;
}

export interface DataTableProps<T> {
  rows: T[];
  columns: Column<T>[];
  rowKey: (row: T) => string;
  onRowClick?: (row: T) => void;
  searchable?: boolean;
  /** Lift the search box out of the table so it can query the server.
   *
   *  Local filtering is fine when the table holds everything. It is actively
   *  misleading when the server truncated the result: typing a name that exists
   *  but fell outside the returned page finds nothing, and the user concludes
   *  the record is missing. Pass these two and the box becomes a server search.
   */
  searchValue?: string;
  onSearchChange?: (value: string) => void;
  searchPlaceholder?: string;
  exportName?: string;
  emptyMessage?: string;
  loading?: boolean;
  footnote?: string;
}

export function DataTable<T>({
  rows, columns, rowKey, onRowClick, searchable = true,
  searchValue, onSearchChange, searchPlaceholder,
  exportName, emptyMessage = "No rows match these filters.", loading, footnote,
}: DataTableProps<T>) {
  const [localQuery, setLocalQuery] = useState("");
  // Controlled when the caller supplies a value; local otherwise.
  const serverSearch = onSearchChange !== undefined;
  const query = serverSearch ? (searchValue ?? "") : localQuery;
  const setQuery = serverSearch ? onSearchChange : setLocalQuery;
  const [sortKey, setSortKey] = useState<string | null>(null);
  const [descending, setDescending] = useState(true);
  const [hidden, setHidden] = useState<Set<string>>(
    () => new Set(columns.filter((c) => c.hidden).map((c) => c.key)),
  );
  const [showColumnPicker, setShowColumnPicker] = useState(false);

  const visible = columns.filter((c) => !hidden.has(c.key));

  const valueOf = (row: T, column: Column<T>): string | number | null => {
    if (column.value) return column.value(row);
    const raw = (row as Record<string, unknown>)[column.key];
    return raw == null ? null : (raw as string | number);
  };

  const filtered = useMemo(() => {
    // Already filtered by the server; filtering again would hide matches whose
    // hit was in a column this table does not render.
    if (serverSearch) return rows;
    if (!query.trim()) return rows;
    const needle = query.toLowerCase();
    return rows.filter((row) =>
      visible.some((column) => String(valueOf(row, column) ?? "").toLowerCase().includes(needle)),
    );
  }, [rows, query, visible]);

  const sorted = useMemo(() => {
    if (!sortKey) return filtered;
    const column = columns.find((c) => c.key === sortKey);
    if (!column) return filtered;
    return [...filtered].sort((a, b) => {
      const left = valueOf(a, column);
      const right = valueOf(b, column);
      if (left == null) return 1;
      if (right == null) return -1;
      if (typeof left === "number" && typeof right === "number") {
        return descending ? right - left : left - right;
      }
      return descending
        ? String(right).localeCompare(String(left))
        : String(left).localeCompare(String(right));
    });
  }, [filtered, sortKey, descending, columns]);

  const exportCsv = () => {
    const header = visible.map((c) => `"${c.header}"`).join(",");
    const body = sorted
      .map((row) => visible.map((c) => `"${String(valueOf(row, c) ?? "").replace(/"/g, '""')}"`).join(","))
      .join("\n");
    const blob = new Blob([`${header}\n${body}`], { type: "text/csv;charset=utf-8" });
    const url = URL.createObjectURL(blob);
    const link = document.createElement("a");
    link.href = url;
    link.download = `${exportName ?? "export"}.csv`;
    link.click();
    URL.revokeObjectURL(url);
  };

  const toggleSort = (column: Column<T>) => {
    if (column.sortable === false) return;
    if (sortKey === column.key) setDescending((d) => !d);
    else { setSortKey(column.key); setDescending(true); }
  };

  return (
    <div className="card overflow-hidden">
      {(searchable || exportName) && (
        <div className="flex flex-wrap items-center gap-2 px-3 py-2.5 border-b border-rule bg-surface-alt">
          {searchable && (
            <label className="relative flex-1 min-w-[180px]">
              <Search size={14} className="absolute left-2.5 top-1/2 -translate-y-1/2 text-ink-faint" />
              <input
                value={query}
                onChange={(e) => setQuery(e.target.value)}
                placeholder={searchPlaceholder ?? "Search"}
                className="w-full pl-8 pr-2 py-1.5 text-sm bg-surface border border-rule rounded-md
                           focus:outline-none focus:ring-2 focus:ring-petrol/30"
              />
            </label>
          )}
          <div className="relative">
            <button className="btn text-xs" onClick={() => setShowColumnPicker((v) => !v)}>
              <Settings2 size={13} /> Columns
            </button>
            {showColumnPicker && (
              <div className="absolute right-0 z-20 mt-1 w-52 card p-2 space-y-1">
                {columns.map((column) => (
                  <label key={column.key} className="flex items-center gap-2 text-xs px-1 py-0.5">
                    <input
                      type="checkbox"
                      checked={!hidden.has(column.key)}
                      onChange={() =>
                        setHidden((prev) => {
                          const next = new Set(prev);
                          next.has(column.key) ? next.delete(column.key) : next.add(column.key);
                          return next;
                        })
                      }
                    />
                    {column.header}
                  </label>
                ))}
              </div>
            )}
          </div>
          {exportName && (
            <button className="btn text-xs" onClick={exportCsv}>
              <Download size={13} /> CSV
            </button>
          )}
        </div>
      )}

      {/* Horizontal scroll is the point on a phone: eleven columns will not
          fit in 390px, and squeezing them makes every one unreadable. The
          container scrolls, never the page body. `overscroll-contain` stops a
          swipe that reaches the end of the table from dragging the page. */}
      <div className="overflow-x-auto overscroll-x-contain max-h-[70vh] lg:max-h-[560px]">
        <table className="w-full text-sm min-w-[640px]">
          <thead className="sticky top-0 z-10">
            <tr>
              {visible.map((column) => (
                <th
                  key={column.key}
                  style={{ width: column.width }}
                  onClick={() => toggleSort(column)}
                  className={`label bg-surface-alt border-b border-rule px-3 py-2 whitespace-nowrap
                              ${column.align === "right" ? "text-right" : "text-left"}
                              ${column.sortable === false ? "" : "cursor-pointer select-none"}`}
                >
                  <span className="inline-flex items-center gap-1">
                    {column.header}
                    {sortKey === column.key && (descending ? <ArrowDown size={10} /> : <ArrowUp size={10} />)}
                  </span>
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {loading &&
              Array.from({ length: 6 }).map((_, i) => (
                <tr key={`sk-${i}`}>
                  {visible.map((c) => (
                    <td key={c.key} className="px-3 py-2.5 border-b border-rule">
                      <div className="skeleton h-3.5 w-full" />
                    </td>
                  ))}
                </tr>
              ))}
            {!loading && sorted.length === 0 && (
              <tr>
                <td colSpan={visible.length} className="px-3 py-10 text-center text-ink-muted text-sm">
                  {emptyMessage}
                </td>
              </tr>
            )}
            {!loading &&
              sorted.map((row) => (
                <tr
                  key={rowKey(row)}
                  onClick={() => onRowClick?.(row)}
                  className={`border-b border-rule last:border-0 hover:bg-surface-alt
                              ${onRowClick ? "cursor-pointer" : ""}`}
                >
                  {visible.map((column) => (
                    <td
                      key={column.key}
                      className={`px-3 py-2.5 align-top ${
                        column.align === "right" ? "text-right font-mono text-[12.5px] tnum" : ""
                      }`}
                    >
                      {column.render ? column.render(row) : String(valueOf(row, column) ?? "—")}
                    </td>
                  ))}
                </tr>
              ))}
          </tbody>
        </table>
      </div>

      {footnote && (
        <p className="px-3 py-2 text-[11.5px] text-ink-faint border-t border-rule">{footnote}</p>
      )}
    </div>
  );
}
